import type { AssetSearchUiState } from '../api/assetSearchClient';
import { normalizeCityKey } from './assetCreatePricingSuggestions';
import { matchVoiceSearchCity } from '../assistant/matchVoiceSearchCity';
import {
  assetMatchesHomeGymCarousel,
  assetMatchesOfficeVoiceSearch,
  assetRecordAssetTypeId,
} from './homeCarouselAssetMatch';
import {
  buildHomeCarouselTypeIdSets,
  buildOfficeVoiceSearchTypeIdSet,
  isHomeGymBrowseTypeSelection,
} from '../constants/filterableAssetTypes';
import { refineAssetsForOpen24hSearch } from './assetOpen24hMatch';
import { listingMatchesAssistantPrice } from '../assistant/assistantPriceConstraint';
import { assetRecordMutationId, assetRecordPrimaryId } from './assetRecordPrimaryId';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function coerceNumericField(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function assetRecordListCountryId(asset: Record<string, unknown>): number | null {
  const from = (o: Record<string, unknown>): number | null => {
    for (const k of ['country_id', 'CountryId', 'countryId'] as const) {
      const n = coerceNumericField(o[k]);
      if (n !== null) {
        return n;
      }
    }
    return null;
  };

  const root = from(asset);
  if (root !== null) {
    return root;
  }

  const addr = asset.address;
  if (isPlainObject(addr)) {
    const n = from(addr);
    if (n !== null) {
      return n;
    }
  }

  for (const nestedKey of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[nestedKey];
    if (isPlainObject(nested)) {
      const n = from(nested);
      if (n !== null) {
        return n;
      }
    }
  }

  const countryObj = asset.country;
  if (isPlainObject(countryObj)) {
    return (
      coerceNumericField(countryObj.id) ??
      coerceNumericField(countryObj.country_id) ??
      from(countryObj)
    );
  }

  return null;
}

function normalizedCountryNameBag(asset: Record<string, unknown>): Set<string> {
  const bag = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return;
    }
    bag.add(raw.trim().toLowerCase());
  };
  add(asset.country_name);
  add(asset.countryName);
  if (typeof asset.country === 'string') {
    add(asset.country);
  }
  if (isPlainObject(asset.country)) {
    const c = asset.country;
    add(c.name);
    add(c.label);
    add(c.country_name);
    add(c.title);
  }
  const addr = asset.address;
  if (isPlainObject(addr)) {
    add(addr.country);
    add(addr.country_name);
    add(addr.countryName);
  }
  for (const nk of ['facility', 'Facility', 'asset'] as const) {
    const f = asset[nk];
    if (isPlainObject(f)) {
      add(f.country_name);
      add(f.countryName);
    }
  }

  inferCountryLabelsFromAddressText(bag, asset);

  return bag;
}

/** Legacy/search rows often omit `country_id` but include an Israeli city or street. */
function inferCountryLabelsFromAddressText(bag: Set<string>, asset: Record<string, unknown>): void {
  const parts: string[] = [];
  const addPart = (raw: unknown) => {
    if (typeof raw === 'string' && raw.trim()) {
      parts.push(raw.trim());
    }
  };
  addPart(asset.address);
  addPart(asset.city);
  addPart(asset.street);
  if (isPlainObject(asset.address)) {
    const addr = asset.address;
    addPart(addr.street);
    addPart(addr.city);
    addPart(addr.line1);
    addPart(addr.line2);
    addPart(typeof addr.formatted === 'string' ? addr.formatted : null);
  }
  for (const nk of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[nk];
    if (isPlainObject(nested)) {
      addPart(nested.city);
      addPart(nested.street);
      addPart(nested.address);
    }
  }
  const text = parts.join(' ').trim();
  if (!text) {
    return;
  }
  // Any known Israel settlement (incl. רמת גן) → treat as Israel when country_id is missing.
  if (matchVoiceSearchCity(text)) {
    bag.add('israel');
    bag.add('ישראל');
  }
}

function districtNumericIdFromObject(o: Record<string, unknown>): number | null {
  for (const k of [
    'state_id',
    'StateId',
    'stateId',
    'district_id',
    'districtId',
    'DistrictId',
    'region_id',
    'regionId',
    'RegionId',
  ] as const) {
    const n = coerceNumericField(o[k]);
    if (n !== null) {
      return n;
    }
  }
  for (const nestKey of ['region', 'Region', 'district', 'District', 'state', 'State'] as const) {
    const nest = o[nestKey];
    if (isPlainObject(nest)) {
      const n =
        coerceNumericField(nest.id) ??
        coerceNumericField(nest.region_id) ??
        coerceNumericField(nest.district_id) ??
        coerceNumericField(nest.state_id);
      if (n !== null) {
        return n;
      }
    }
  }
  return null;
}

function assetRecordListDistrictFilterId(asset: Record<string, unknown>): number | null {
  const root = districtNumericIdFromObject(asset);
  if (root !== null) {
    return root;
  }
  const addr = asset.address;
  if (isPlainObject(addr)) {
    const n = districtNumericIdFromObject(addr);
    if (n !== null) {
      return n;
    }
  }
  for (const nk of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[nk];
    if (isPlainObject(nested)) {
      const n = districtNumericIdFromObject(nested);
      if (n !== null) {
        return n;
      }
    }
  }
  return null;
}

function normalizedDistrictNameBag(asset: Record<string, unknown>): Set<string> {
  const bag = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return;
    }
    bag.add(raw.trim().toLowerCase());
  };
  add(asset.state_name);
  add(asset.stateName);
  add(asset.district_name);
  add(asset.districtName);
  add(asset.region_name);
  add(asset.regionName);

  const maybeObjs = [
    asset.address,
    asset.region,
    asset.Region,
    asset.district,
    asset.District,
    asset.state,
    asset.State,
    asset.facility,
    asset.Facility,
  ];
  for (const mo of maybeObjs) {
    if (isPlainObject(mo)) {
      add(mo.name);
      add(mo.label);
      add(mo.title);
      add(mo.region_name);
      add(mo.state_name);
      add(mo.district_name);
    }
  }
  return bag;
}

function normalizedAssetTypeNameBag(asset: Record<string, unknown>): Set<string> {
  const bag = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return;
    }
    bag.add(raw.trim().toLowerCase());
  };
  add(asset.asset_type_name);
  add(asset.assetTypeName);
  add(asset.type_name);
  add(asset.type);
  if (typeof asset.asset_type === 'string') {
    add(asset.asset_type);
  }
  if (isPlainObject(asset.asset_type)) {
    const o = asset.asset_type;
    add(o.name);
    add(o.label);
    add(o.type_name);
    add(o.title);
  }
  return bag;
}

function assetRowMatchesSelectedCountry(
  asset: Record<string, unknown>,
  wantId: number,
  catalogLabelLower: string | null,
): boolean {
  const cid = assetRecordListCountryId(asset);
  if (cid !== null && cid === wantId) {
    return true;
  }
  if (catalogLabelLower !== null && normalizedCountryNameBag(asset).has(catalogLabelLower)) {
    return true;
  }
  return false;
}

function assetRowMatchesSelectedDistrict(
  asset: Record<string, unknown>,
  wantId: number,
  catalogLabelLower: string | null,
): boolean {
  const rowId = assetRecordListDistrictFilterId(asset);
  if (rowId !== null && rowId === wantId) {
    return true;
  }
  if (!catalogLabelLower) {
    return false;
  }
  const names = normalizedDistrictNameBag(asset);
  if (names.has(catalogLabelLower)) {
    return true;
  }
  for (const n of Array.from(names)) {
    if (catalogLabelLower.length >= 2 && (n.includes(catalogLabelLower) || catalogLabelLower.includes(n))) {
      return true;
    }
  }
  return false;
}

function assetRowMatchesSelectedAssetType(
  asset: Record<string, unknown>,
  wantId: number,
  catalogLabelLower: string | null,
): boolean {
  const rowId = assetRecordAssetTypeId(asset);
  if (rowId !== null && rowId === wantId) {
    return true;
  }
  if (catalogLabelLower) {
    const names = normalizedAssetTypeNameBag(asset);
    if (names.has(catalogLabelLower)) {
      return true;
    }
    for (const n of Array.from(names)) {
      if (n.includes(catalogLabelLower) || catalogLabelLower.includes(n)) {
        return true;
      }
    }
  }
  return false;
}

function cityHeadKey(city: string): string {
  return normalizeCityKey(city.split(/\s*[-–]\s*/)[0] ?? city);
}

function assetRecordCityValues(asset: Record<string, unknown>): string[] {
  const values: string[] = [];
  const add = (raw: unknown) => {
    if (typeof raw === 'string' && raw.trim()) {
      values.push(raw.trim());
    }
  };
  add(asset.city);
  if (typeof asset.address === 'string') {
    add(asset.address);
  }
  if (isPlainObject(asset.address)) {
    add(asset.address.city);
    add(asset.address.formatted);
    add(asset.address.line1);
    add(asset.address.full);
  }
  for (const key of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[key];
    if (isPlainObject(nested)) {
      add(nested.city);
      if (typeof nested.address === 'string') {
        add(nested.address);
      }
      if (isPlainObject(nested.address)) {
        add(nested.address.city);
        add(nested.address.formatted);
        add(nested.address.line1);
      }
    }
  }
  return values;
}

function assetRecordStreetValues(asset: Record<string, unknown>): string[] {
  const values: string[] = [];
  const add = (raw: unknown) => {
    if (typeof raw === 'string' && raw.trim()) {
      values.push(raw.trim());
    }
  };
  add(asset.street);
  if (typeof asset.address === 'string') {
    add(asset.address);
  }
  if (isPlainObject(asset.address)) {
    add(asset.address.street);
    add(asset.address.line1);
    add(asset.address.formatted);
  }
  for (const key of ['facility', 'Facility', 'asset'] as const) {
    const nested = asset[key];
    if (isPlainObject(nested)) {
      add(nested.street);
      if (isPlainObject(nested.address)) {
        add(nested.address.street);
        add(nested.address.line1);
      }
    }
  }
  return values;
}

export function assetRecordMatchesBrowseCity(
  asset: Record<string, unknown>,
  city: string,
): boolean {
  const wantFull = normalizeCityKey(city);
  const wantHead = cityHeadKey(city);
  const wantHe = matchVoiceSearchCity(city)?.cityHe ?? null;
  if (!wantHead) {
    return true;
  }
  for (const value of assetRecordCityValues(asset)) {
    const haveFull = normalizeCityKey(value);
    const haveHead = cityHeadKey(value);
    if (haveFull === wantFull || haveHead === wantHead) {
      return true;
    }
    if (haveFull.includes(wantHead) || wantFull.includes(haveHead)) {
      return true;
    }
    const haveHe = matchVoiceSearchCity(value)?.cityHe ?? null;
    if (wantHe && haveHe && wantHe === haveHe) {
      return true;
    }
  }
  return false;
}

function stripHebrewArticleToken(token: string): string {
  if (/^ה[\u0590-\u05ff]{2,}/.test(token)) {
    return token.slice(1);
  }
  return token;
}

function streetCoreTokens(value: string): string[] {
  const tokens = normalizeCityKey(value)
    .split(/[^a-z0-9\u0590-\u05ff]+/i)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2 && token !== 'street' && token !== 'st' && token !== 'רחוב')
    .map((token) => stripHebrewArticleToken(token).replace(/יי/g, 'י'));
  return Array.from(new Set(tokens));
}

export function assetRecordMatchesBrowseStreet(
  asset: Record<string, unknown>,
  street: string,
): boolean {
  const want = streetCoreTokens(street);
  if (want.length === 0) {
    return true;
  }
  for (const value of assetRecordStreetValues(asset)) {
    const have = new Set(streetCoreTokens(value));
    if (want.every((token) => have.has(token))) {
      return true;
    }
  }
  return false;
}

/** True when country, region, type, city, or street must be applied after the search API response. */
export function hasClientSideBrowseFilters(state: AssetSearchUiState): boolean {
  if (finiteNumber(state.countryId) !== null) {
    return true;
  }
  if (finiteNumber(state.stateId) !== null) {
    return true;
  }
  if (finiteNumber(state.assetTypeId) !== null) {
    return true;
  }
  if (state.assetTypeIds?.length) {
    return true;
  }
  if (state.city?.trim()) {
    return true;
  }
  if (state.street?.trim()) {
    return true;
  }
  if (state.open24h) {
    return true;
  }
  if (state.amenityIcons && state.amenityIcons.length > 0) {
    return true;
  }
  if (
    state.schedule &&
    (state.schedule.weekdays.length > 0 ||
      (state.schedule.closed_weekdays?.length ?? 0) > 0) &&
    !state.schedule.start_time
  ) {
    return true;
  }
  if (state.assetTypeFamily === 'office' || state.assetTypeFamily === 'gym') {
    return true;
  }
  if (state.assetTypeLabelLower?.trim()) {
    return true;
  }
  if (state.assetIds && state.assetIds.length > 0) {
    return true;
  }
  if (state.price) {
    return true;
  }
  return false;
}

function collectAssetRecordIdKeys(asset: Record<string, unknown>): string[] {
  const keys: string[] = [];
  const add = (raw: string | null) => {
    const id = raw?.trim();
    if (id) {
      keys.push(id.toLowerCase());
    }
  };
  add(assetRecordPrimaryId(asset));
  add(assetRecordMutationId(asset));
  return keys;
}

/**
 * Restrict to backend `asset_ids` when the caller asked for them.
 * Returns null when ids are absent (caller uses the full record list).
 * When ids were requested, returns the matched rows — possibly empty —
 * and never falls through to the rest of the catalog.
 * When a city is also set, keep only those id rows that match the city.
 */
export function preferRecordsMatchingAssetIds(
  records: Record<string, unknown>[],
  assetIds: string[] | null | undefined,
  city?: string | null,
): Record<string, unknown>[] | null {
  const wanted = (assetIds ?? []).map((id) => id.trim()).filter(Boolean);
  if (wanted.length === 0) {
    return null;
  }
  const byKey = new Map<string, Record<string, unknown>>();
  for (const row of records) {
    for (const key of collectAssetRecordIdKeys(row)) {
      if (!byKey.has(key)) {
        byKey.set(key, row);
      }
    }
  }
  const matched: Record<string, unknown>[] = [];
  const seen = new Set<Record<string, unknown>>();
  for (const id of wanted) {
    const row = byKey.get(id.toLowerCase());
    if (row && !seen.has(row)) {
      seen.add(row);
      matched.push(row);
    }
  }
  const cityTrim = city?.trim() ?? '';
  if (!cityTrim) {
    return matched;
  }
  return matched.filter((row) => assetRecordMatchesBrowseCity(row, cityTrim));
}

export function filterAssetRecordsForUiState(
  records: Record<string, unknown>[],
  state: AssetSearchUiState,
): Record<string, unknown>[] {
  const preferred = preferRecordsMatchingAssetIds(records, state.assetIds, state.city);
  let rows = preferred !== null ? preferred : records;

  const countryId = finiteNumber(state.countryId);
  if (countryId !== null) {
    const label = state.countryLabelLower?.trim().toLowerCase() ?? null;
    rows = rows.filter((a) => assetRowMatchesSelectedCountry(a, countryId, label));
  }

  const stateId = finiteNumber(state.stateId);
  if (stateId !== null) {
    const label = state.stateLabelLower?.trim().toLowerCase() ?? null;
    rows = rows.filter((a) => assetRowMatchesSelectedDistrict(a, stateId, label));
  }

  const typeIds = state.assetTypeIds?.filter((id) => Number.isFinite(id) && id > 0) ?? [];
  const catalog = state.assetTypesCatalog ?? [];
  if (state.assetTypeFamily === 'office') {
    const officeIds = buildOfficeVoiceSearchTypeIdSet(catalog);
    rows = rows.filter((a) => assetMatchesOfficeVoiceSearch(a, catalog, officeIds));
  } else if (state.assetTypeFamily === 'gym' || typeIds.length > 1) {
    const { gymIds } = buildHomeCarouselTypeIdSets(catalog);
    rows = rows.filter((a) => assetMatchesHomeGymCarousel(a, catalog, gymIds));
  } else if (typeIds.length === 1) {
    const want = typeIds[0];
    const label = state.assetTypeLabelLower?.trim().toLowerCase() ?? null;
    rows = rows.filter((a) => assetRowMatchesSelectedAssetType(a, want, label));
  } else {
    const typeId = finiteNumber(state.assetTypeId);
    if (typeId !== null) {
      const label = state.assetTypeLabelLower?.trim().toLowerCase() ?? null;
      if (isHomeGymBrowseTypeSelection(typeId, label)) {
        const { gymIds } = buildHomeCarouselTypeIdSets(catalog);
        rows = rows.filter((a) => assetMatchesHomeGymCarousel(a, catalog, gymIds));
      } else {
        rows = rows.filter((a) => assetRowMatchesSelectedAssetType(a, typeId, label));
      }
    } else if (state.assetTypeLabelLower?.trim()) {
      const label = state.assetTypeLabelLower.trim().toLowerCase();
      rows = rows.filter((a) => assetRowMatchesSelectedAssetType(a, -1, label));
    }
  }

  const city = state.city?.trim() ?? '';
  if (city) {
    rows = rows.filter((a) => assetRecordMatchesBrowseCity(a, city));
  }

  const street = state.street?.trim() ?? '';
  if (street) {
    rows = rows.filter((a) => assetRecordMatchesBrowseStreet(a, street));
  }

  if (state.open24h) {
    rows = refineAssetsForOpen24hSearch(rows, state.open24hFacilityIds ?? []);
  }

  if (state.price) {
    rows = rows.filter((a) => listingMatchesAssistantPrice(a, state.price));
  }

  return rows;
}
